<script setup lang="ts">
  import { ref, watch } from 'vue';
  import { FilterModal, SearchCombobox, ThemeProvider } from '@/shared/ui';
  import { formatDateForApi, parseCivilDate } from '@/shared/lib';
  import { useMemberFilterOptions } from '../model/useMemberFilterOptions';
  import FilterDateField from './FilterDateField.vue';
  import type { DraftsListFilters } from '../lib/drafts-list-query';

  const props = defineProps<{
    currentFilters?: DraftsListFilters;
  }>();

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: DraftsListFilters];
    'clear-filters': [];
  }>();

  const draftDate = ref<Date | null>(null);
  const draftCreatedBy = ref('');

  const {
    options: memberComboboxOptions,
    isLoading: isMembersLoading,
    searchQuery: memberSearchQuery,
  } = useMemberFilterOptions();

  const loadFromFilters = (filters?: DraftsListFilters) => {
    draftDate.value = parseCivilDate(filters?.updatedAt);
    draftCreatedBy.value = filters?.createdBy ? String(filters.createdBy) : '';
  };

  loadFromFilters(props.currentFilters);

  watch(() => props.currentFilters, loadFromFilters, { deep: true });

  const handleApply = () => {
    emit('update:filter', {
      updatedAt: draftDate.value ? (formatDateForApi(draftDate.value) ?? undefined) : undefined,
      createdBy: draftCreatedBy.value ? Number(draftCreatedBy.value) : undefined,
    });
  };

  const handleClear = () => {
    draftDate.value = null;
    draftCreatedBy.value = '';
    memberSearchQuery.value = '';
    emit('clear-filters');
  };

  const handleClose = () => {
    loadFromFilters(props.currentFilters);
    emit('close');
  };
</script>

<template>
  <FilterModal
    content-class="w-auto max-w-sm"
    @close="handleClose"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="flex flex-col gap-4 px-6 py-5">
          <FilterDateField v-model="draftDate" trigger-test-id="drafts-filter-date-trigger" />

          <div class="flex flex-col gap-1.5">
            <h4 class="text-sm font-medium text-gray-07">Criado por</h4>
            <SearchCombobox
              :model-value="draftCreatedBy"
              :options="memberComboboxOptions"
              :loading="isMembersLoading"
              placeholder="Selecione um colaborador"
              search-placeholder="Buscar colaborador..."
              trigger-icon="arrow-down"
              class="w-full h-11"
              data-testid="drafts-filter-creator-combobox"
              @update:model-value="draftCreatedBy = $event || ''"
              @search="memberSearchQuery = $event"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
